/**
 * PHEV Cost Calculation Module
 *
 * Implements the calculation logic for "Total Cost to Run Mazda PHEV"
 * based on the specification in documentation/readme.md
 */

/**
 * NRCan conversion factor: 1 L of gasoline contains the energy equivalent of
 * 8.9 kWh of electricity. Used to turn an Le rating (L/100km) into kWh/100km.
 */
export const LE_TO_KWH_FACTOR = 8.9;

/** Weekday days in an average month, used for monthly extrapolation. */
export const WEEKDAY_DAYS_PER_MONTH = 21.67;

/** Weekend days in an average month, used for monthly extrapolation. */
export const WEEKEND_DAYS_PER_MONTH = 8.67;

/**
 * Mandatory unit conversion. Convert a fuel-consumption (Le) rating expressed
 * in L/100km into an electrical efficiency expressed in kWh/100km.
 */
export function convertLeToKwh(leRating: number): number {
  return leRating * LE_TO_KWH_FACTOR;
}

/**
 * Input parameters required for PHEV cost calculations
 */
export interface PHEVCalculationInput {
  /** Daily driving distance in kilometers */
  dailyKM: number;
  /** PHEV's all-electric range in kilometers */
  phevElectricRange: number;
  /** PHEV's fuel economy in hybrid mode (L/100km) */
  phevHybridEconomy: number;
  /** Fuel cost in $/L */
  fuelCost: number;
  /** Electricity cost in $/kWh */
  electricityCost: number;
  /** PHEV's Le rating in L/100km; converted to kWh/100km via * 8.9 */
  phevElectricEfficiency: number;
}

/**
 * Output of a PHEV daily cost calculation
 */
export interface PHEVCalculationResult {
  /** Distance covered by electric power (km) */
  electricKM: number;
  /** Distance covered by hybrid/gasoline power (km) */
  hybridKM: number;
  /** Cost of the hybrid portion of the journey ($) */
  hybridCost: number;
  /** Cost of the electric portion of the journey ($) */
  electricCost: number;
  /** Total daily cost to run the PHEV ($) */
  totalDailyCost: number;
  /** Total monthly cost (30 days) */
  totalMonthlyCost: number;
  /** Total yearly cost (365 days) */
  totalYearlyCost: number;
}

/**
 * Calculate the distance covered by electric power for a single day
 *
 * @param dailyKM - The daily driving distance
 * @param phevElectricRange - The PHEV's all-electric range
 * @returns The distance covered by electric power (km)
 */
export function calculateElectricKM(
  dailyKM: number,
  phevElectricRange: number
): number {
  return Math.min(dailyKM, phevElectricRange);
}

/**
 * Calculate the distance covered by hybrid/gasoline power for a single day
 *
 * @param dailyKM - The daily driving distance
 * @param electricKM - The distance already covered by electric power
 * @returns The remaining distance to be covered by hybrid/gasoline power (km)
 */
export function calculateHybridKM(
  dailyKM: number,
  electricKM: number
): number {
  return Math.max(0, dailyKM - electricKM);
}

/**
 * Calculate the cost of the hybrid/gasoline portion of the journey
 *
 * Formula: (Hybrid_KM / 100) * PHEV_Hybrid_Economy * Fuel_Cost
 *
 * @param hybridKM - Distance covered by hybrid power
 * @param phevHybridEconomy - Fuel economy in hybrid mode (L/100km)
 * @param fuelCost - Fuel cost ($/L)
 * @returns The cost of the hybrid portion ($)
 */
export function calculateHybridCost(
  hybridKM: number,
  phevHybridEconomy: number,
  fuelCost: number
): number {
  return (hybridKM / 100) * phevHybridEconomy * fuelCost;
}

/**
 * Calculate the cost of the electric portion of the journey
 *
 * Formula: (Electric_KM / 100) * (PHEV_Le_Rating * 8.9) * Electricity_Cost
 *
 * The Le rating (L/100km) is first converted to kWh/100km via the 8.9 factor.
 *
 * @param electricKM - Distance covered by electric power
 * @param phevElectricEfficiency - PHEV's Le rating (L/100km)
 * @param electricityCost - Electricity cost ($/kWh)
 * @returns The cost of the electric portion ($)
 */
export function calculateElectricCost(
  electricKM: number,
  phevElectricEfficiency: number,
  electricityCost: number
): number {
  return (electricKM / 100) * convertLeToKwh(phevElectricEfficiency) * electricityCost;
}

/**
 * Monthly average cost for a weekday/weekend pattern.
 * Monthly = (Weekday_Daily_Cost * 21.67) + (Weekend_Daily_Cost * 8.67)
 */
export function calculateMonthlyCost(weekdayDailyCost: number, weekendDailyCost: number): number {
  return weekdayDailyCost * WEEKDAY_DAYS_PER_MONTH + weekendDailyCost * WEEKEND_DAYS_PER_MONTH;
}

/**
 * Yearly total = 12 x the average monthly cost.
 */
export function calculateYearlyCost(weekdayDailyCost: number, weekendDailyCost: number): number {
  return calculateMonthlyCost(weekdayDailyCost, weekendDailyCost) * 12;
}

/**
 * Calculate the total daily cost to run the PHEV
 *
 * @param hybridCost - Cost of the hybrid portion
 * @param electricCost - Cost of the electric portion
 * @returns The total daily cost ($)
 */
export function calculateTotalDailyCost(
  hybridCost: number,
  electricCost: number
): number {
  return hybridCost + electricCost;
}

/**
 * Calculate the complete PHEV cost breakdown for a single day,
 * including monthly and yearly extrapolations
 *
 * @param input - The calculation input parameters
 * @returns The complete calculation result
 * @throws Error if any required input is negative or missing
 */
export function calculatePHEVCostPerDay(
  input: PHEVCalculationInput
): PHEVCalculationResult {
  // Validate inputs
  validateInputs(input);

  // Step 1: Determine distance covered by each power source
  const electricKM = calculateElectricKM(input.dailyKM, input.phevElectricRange);
  const hybridKM = calculateHybridKM(input.dailyKM, electricKM);

  // Step 2: Calculate cost for each portion
  const hybridCost = calculateHybridCost(
    hybridKM,
    input.phevHybridEconomy,
    input.fuelCost
  );
  const electricCost = calculateElectricCost(
    electricKM,
    input.phevElectricEfficiency,
    input.electricityCost
  );

  // Step 3: Calculate total daily cost
  const totalDailyCost = calculateTotalDailyCost(hybridCost, electricCost);

  // Step 4: Extrapolate using the monthly rule (21.67 weekdays + 8.67 weekends)
  const totalDailyCostRounded = Number(totalDailyCost.toFixed(2));

  return {
    electricKM,
    hybridKM,
    hybridCost: Number(hybridCost.toFixed(2)),
    electricCost: Number(electricCost.toFixed(2)),
    totalDailyCost: totalDailyCostRounded,
    totalMonthlyCost: Number(calculateMonthlyCost(totalDailyCostRounded, totalDailyCostRounded).toFixed(2)),
    totalYearlyCost: Number(calculateYearlyCost(totalDailyCostRounded, totalDailyCostRounded).toFixed(2)),
  };
}

export function formatCurrency(value: number, currencySymbol: string, locale: string): string {
  const formatted = value.toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (locale === 'fr') {
    // Space thousands grouping, comma decimal separator, symbol after
    return `${formatted.replace(/,/g, ' ').replace(/\./g, ',')}${currencySymbol}`;
  }
  return `${currencySymbol}${formatted}`;
}

export function formatCurrencyWhole(value: number, currencySymbol: string, locale: string): string {
  const formatted = value.toLocaleString('en-CA', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  if (locale === 'fr') {
    return `${formatted.replace(/,/g, ' ')}${currencySymbol}`;
  }
  return `${currencySymbol}${formatted}`;
}

/**
 * Validate that all input values are non-negative and valid
 *
 * @param input - The calculation input to validate
 * @throws Error if any input is invalid
 */
function validateInputs(input: PHEVCalculationInput): void {
  const errors: string[] = [];

  if (input.dailyKM < 0) {
    errors.push("dailyKM must be non-negative");
  }
  if (input.phevElectricRange < 0) {
    errors.push("phevElectricRange must be non-negative");
  }
  if (input.phevHybridEconomy < 0) {
    errors.push("phevHybridEconomy must be non-negative");
  }
  if (input.fuelCost < 0) {
    errors.push("fuelCost must be non-negative");
  }
  if (input.electricityCost < 0) {
    errors.push("electricityCost must be non-negative");
  }
  if (input.phevElectricEfficiency < 0) {
    errors.push("phevElectricEfficiency must be non-negative");
  }

  if (errors.length > 0) {
    throw new Error(`Invalid PHEV calculation inputs:\n${errors.join("\n")}`);
  }
}
